/**
 * 動的価格計算システム - メタフィールドベース
 * 
 * 商品のメタフィールドから価格データを読み込み、
 * ユーザーの選択に応じて動的に価格を計算します。
 * 
 * @class DynamicPriceCalculator
 * @author 株式会社丸庄
 * @version 1.1.0 - pageCount filtering support
 * @date 2025-12-03
 */

class DynamicPriceCalculator {
  /**
   * コンストラクタ
   * @param {Object} productData - メタフィールドから読み込んだ商品データ
   */
  constructor(productData) {
    
    if (!productData) {
      throw new Error('Product data is required');
    }
    
    // メタフィールドデータを格納
    this.pricingConfig = productData.pricing_config || {};
    this.priceMatrix = productData.price_matrix || {};
    this.optionMultipliers = productData.option_multipliers || {};
    this.calculationRules = productData.calculation_rules || {};
    // variation_code生成設定（設定がある場合のみ）
    this.variationCodeConfig = productData.variation_code_config || null;
    
    // 価格データを直接アクセス可能にする
    this.priceData = this.priceMatrix.price_data || [];
    
    // データの検証
    this.validateData();
    
    // 現在の選択状態を初期化
    this.currentSelection = this.initializeSelection();
    
    // UIの初期化
    this.initializeUI();
    
  }
  
  /**
   * データの検証
   */
  validateData() {

    // 必須データの確認（遅延ロード中は警告を抑制）
    const isLazyLoading = window.needsLazyLoad && !window._lazyLoadDataReady;
    if (!this.priceMatrix || !this.priceMatrix.price_data || this.priceMatrix.price_data.length === 0) {
      if (!isLazyLoading) {
        console.warn('[DynamicPriceCalculator] Price matrix is empty or invalid');
      }
    }

    if (!this.optionMultipliers && !isLazyLoading) {
      console.warn('[DynamicPriceCalculator] Option multipliers not found');
    }

  }
  
  /**
   * 利用可能な用紙リストを取得
   * @returns {Array<string>} 用紙名の配列
   */
  getAvailablePapers() {
    const papersSet = new Set();
    
    // Shopifyオプションから用紙リストを取得（優先）
    if (window.productData && window.productData.shopify_options) {
      const paperOpt = window.productData.shopify_options.find(o => o.name === '用紙');
      if (paperOpt && paperOpt.values && paperOpt.values.length > 0) {
        paperOpt.values.forEach(v => papersSet.add(v));
        return Array.from(papersSet);
      }
    }
    
    // fallback: price_dataから用紙を抽出
    const priceData = this.priceData || [];
    const hasPageCount = (window.productData && window.productData.shopify_options && 
                         window.productData.shopify_options.some(o => o.name === 'ページ数' || o.name === 'ページ範囲')) ||
                         (window.pageCountOptions && window.pageCountOptions.options);
    
    priceData.forEach(entry => {
      if (entry.variation_code) {
        if (entry.variation_code.includes(window.VARIATION_CODE_SEPARATOR)) {
          const parts = entry.variation_code.split(window.VARIATION_CODE_SEPARATOR);
          // ページ数オプションがある場合: ページ数_印刷色数_用紙 の形式
          // ない場合: 印刷色数_用紙 または 用紙_印刷色数 の形式
          if (hasPageCount && parts.length >= 3) {
            papersSet.add(parts.slice(2).join(window.VARIATION_CODE_SEPARATOR)); // 用紙名（最後の部分）
          } else if (parts.length >= 2) {
            // 従来の形式: 最初のパーツを用紙として扱う
            papersSet.add(parts[0]);
          }
        } else if (entry.variation_code !== window.VARIATION_CODE_STANDARD) {
          papersSet.add(entry.variation_code);
        }
      }
    });
    
    return Array.from(papersSet).sort();
  }
  
  /**
   * 選択状態の初期化
   * @returns {Object} 初期選択状態
   */
  initializeSelection() {
    
    const sizeOptions = this.pricingConfig.size_options || {};
    const paperOptions = this.pricingConfig.paper_options || {};
    const printColorOptions = this.pricingConfig.print_color_options || {};
    const quantityRanges = this.pricingConfig.quantity_ranges || {};
    
    const printMethodOptions = this.pricingConfig.print_method_options || {};
    
    // 利用可能な用紙リストを抽出（実際に価格データがある用紙のみ）
    const availablePapers = this.getAvailablePapers();
    const firstAvailablePaper = availablePapers.length > 0 ? availablePapers[0] : null;
    
    // URLパラメータから選択状態を取得
    const urlParams = new URLSearchParams(window.location.search);

    // ★冊子商品判定: Shopifyオプションが「本文紙質」または「ページ範囲」のみで
    //   「用紙」「印刷色数」を持たない場合、paper/printColor を初期化してはいけない。
    //   （表紙紙質・ページ範囲が paper/printColor に誤って入ると variation_code が壊れる）
    const __shopifyOpts = (typeof window !== 'undefined' && window.productData?.shopify_options) || [];
    const __hasBodyPaperOpt = __shopifyOpts.some(o => o && o.name === '本文紙質');
    const __hasPageRangeOpt = __shopifyOpts.some(o => o && o.name === 'ページ範囲');
    const __hasPaperOpt = __shopifyOpts.some(o => o && o.name === '用紙');
    const __hasPrintColorOpt = __shopifyOpts.some(o => o && o.name === '印刷色数');
    const __isBookletLike = (__hasBodyPaperOpt || __hasPageRangeOpt) && !__hasPaperOpt && !__hasPrintColorOpt;

    // 用紙の初期値を決定
    let initialPaper;
    if (__isBookletLike) {
      // 冊子商品は paper を使わない（coverPaper/bodyPaper で管理）
      initialPaper = '';
    } else if (paperOptions.enabled === false) {
      initialPaper = '';
    } else if (urlParams.get('paper')) {
      initialPaper = urlParams.get('paper');
    } else if (paperOptions.default_paper) {
      initialPaper = paperOptions.default_paper;
    } else if (firstAvailablePaper) {
      // pricingConfigがない場合は価格データから取得した最初の用紙を使用
      initialPaper = firstAvailablePaper;
    } else {
      initialPaper = '';
    }
    
    // 印刷色の初期値を決定
    // 【重要】enabled=falseはUIセレクターを非表示にするだけで、
    // default_optionがある場合はその値を使用する（variation_code構築に必要）
    let initialPrintColor;
    if (__isBookletLike) {
      // 冊子商品は printColor を使わない
      initialPrintColor = null;
    } else if (printColorOptions.default_option) {
      // default_optionが設定されている場合は、enabledに関係なくその値を使用
      initialPrintColor = urlParams.get('printColor') || printColorOptions.default_option;
    } else if (printColorOptions.enabled === false) {
      // enabled=falseでdefault_optionがない場合はnull
      initialPrintColor = null;
    } else {
      // enabledだがdefault_optionが空の場合はnull（モノクロ商品等）
      initialPrintColor = null;
    }
    
    // 利用可能なバリアント組み合わせをチェックして、存在しない組み合わせの場合は自動調整
    const combinations = window.availableVariantCombinations || (typeof window !== 'undefined' && window.productData?.availableVariantCombinations);
    if (combinations && combinations.byPaper && initialPaper && initialPrintColor) {
      const availablePrintColorsForPaper = combinations.byPaper[initialPaper] || [];
      
      if (availablePrintColorsForPaper.length > 0 && !availablePrintColorsForPaper.includes(initialPrintColor)) {
        // 現在の印刷色数が利用不可の場合、最初の利用可能な印刷色数に変更
        const newPrintColor = availablePrintColorsForPaper[0];
        initialPrintColor = newPrintColor;
      }
    }
    
    // 数量の初期値を決定（価格データから取得）
    let initialQuantity = parseInt(urlParams.get('quantity')) || quantityRanges.default_quantity || 10;
    const availableQuantities = this.getAvailableQuantities(initialPaper, initialPrintColor);
    if (availableQuantities.length > 0 && !availableQuantities.includes(initialQuantity)) {
      initialQuantity = availableQuantities[0];
    }
    
    // 納期の初期値を決定（価格データから取得）
    let initialDelivery = urlParams.get('delivery') || '3営業日';
    const availableDeliveries = this.getAvailableDeliveries(initialPaper, initialPrintColor, initialQuantity);
    if (availableDeliveries.length > 0 && !availableDeliveries.includes(initialDelivery)) {
      initialDelivery = availableDeliveries[0];
    }
    
    // ページ数の初期値を決定（page_count_optionsがある場合）
    let initialPageCount = null;
    if (typeof window !== 'undefined' && window.pageCountOptions && window.pageCountOptions.options && window.pageCountOptions.options.length > 0) {
      // 両フォーマットに対応: 数値配列 [8, 12, 16] または オブジェクト配列 [{value: "8", label: "8ページ"}]
      const rawDefault = window.pageCountOptions.default || window.pageCountOptions.options[0];
      const pageCountDefault = (typeof rawDefault === 'object' && rawDefault !== null) 
        ? (parseInt(rawDefault.value) || rawDefault.value) 
        : rawDefault;
      initialPageCount = parseInt(urlParams.get('pageCount')) || parseInt(pageCountDefault);
    }
    
    // 印刷方法の初期値を決定（商品ページの実際の印刷方法を優先）
    let initialPrintMethod;
    if (urlParams.get('printMethod')) {
      initialPrintMethod = urlParams.get('printMethod');
    } else if (window.productGroupInfo && window.productGroupInfo.currentPrintType) {
      // 商品ページの実際の印刷方法を使用
      initialPrintMethod = window.productGroupInfo.currentPrintType;
    } else if (printMethodOptions.default_method) {
      initialPrintMethod = printMethodOptions.default_method;
    } else {
      // デフォルトはオンデマンド（後方互換性のため）
      initialPrintMethod = 'オンデマンド';
    }
    
    // 加工オプションの初期値（URLパラメータから取得）
    let initialProcessing = [];
    const processingParam = urlParams.get('processing');
    if (processingParam) {
      initialProcessing = processingParam.split(',').map(p => p.trim()).filter(p => p);
    }
    
    // データ種別の初期値（URLパラメータから取得）
    const initialDataType = urlParams.get('dataType') || 'pdf_professional';
    
    // カスタムサイズの初期値（URLパラメータから取得）
    let initialCustomSize = null;
    const customWidth = urlParams.get('customWidth');
    const customHeight = urlParams.get('customHeight');
    if (customWidth && customHeight) {
      const parsedWidth = parseInt(customWidth, 10);
      const parsedHeight = parseInt(customHeight, 10);
      // NaNチェック：有効な数値の場合のみ設定
      if (!isNaN(parsedWidth) && !isNaN(parsedHeight)) {
        initialCustomSize = {
          width: parsedWidth,
          height: parsedHeight
        };
      }
    }
    
    // サイズの初期値を決定（商品ページの実際のサイズを優先）
    // size_only時は価格データから最初のバリエーションコードを使用
    let initialSize = null;
    if (urlParams.get('size')) {
      initialSize = urlParams.get('size');
    } else if (window.productGroupInfo && (window.productGroupInfo.currentSize || window.productGroupInfo.size)) {
      // 商品タグから取得したサイズを使用（最優先）
      initialSize = window.productGroupInfo.currentSize || window.productGroupInfo.size;
    } else if (sizeOptions.default_size) {
      initialSize = sizeOptions.default_size;
    }

    // size_only（伝票印刷等）の場合、paperから初期値を取得
    if (!initialSize && this.variationCodeConfig?.format === 'size_only') {
      initialSize = initialPaper || this.getFirstAvailableVariationCode();
    }

    // 最終フォールバック
    if (!initialSize) {
      initialSize = 'A3';
    }
    
    const selection = {
      // サイズ
      size: initialSize,
      
      // 印刷方法
      printMethod: initialPrintMethod,
      
      // 用紙
      paper: initialPaper,
      
      // 印刷色
      printColor: initialPrintColor,
      
      // 数量
      quantity: initialQuantity,
      
      // 納期
      delivery: initialDelivery,
      
      // ページ数（中綴じ等で使用）
      pageCount: initialPageCount,
      
      // 加工オプション（複数選択可能）
      processing: initialProcessing,
      
      // データ種別
      dataType: initialDataType,
      
      // カスタムサイズ（該当する場合）
      customSize: initialCustomSize,
      
      // オプション調整値（ProductOptionsManagerから設定される）
      optionPriceAdjustment: 0,
      optionLeadTimeAdjustment: 0
    };
    
    
    // ★ 再注文フラグがある場合はログを出力
    if (urlParams.get('reorder') === 'true') {
    }
    
    return selection;
  }
  
  /**
   * 指定された用紙と印刷色で利用可能な数量リストを取得
   */
  getAvailableQuantities(paper, printColor) {
    // 遅延ロード後のデータも含めて最新のprice_dataを参照
    const priceData = (this.priceMatrix && this.priceMatrix.price_data) || this.priceData || [];
    const variationCode = this.buildVariationCode(paper, printColor);

    const quantities = new Set();
    priceData.forEach(entry => {
      if (entry.variation_code === variationCode && entry.quantity) {
        quantities.add(entry.quantity);
      }
    });

    let result = Array.from(quantities).sort((a, b) => a - b);

    // PrintHubで設定された有効部数でフィルタリング（既定選択が非表示部数にならないようにする）
    if (window.enabledQuantities && Array.isArray(window.enabledQuantities) && window.enabledQuantities.length > 0) {
      const enabledQtySet = new Set(window.enabledQuantities.map(q => Number(q)));
      const filtered = result.filter(q => enabledQtySet.has(Number(q)));
      if (filtered.length > 0) {
        result = filtered;
      }
    }

    return result;
  }
  
  /**
   * 指定された条件で利用可能な納期リストを取得
   */
  getAvailableDeliveries(paper, printColor, quantity) {
    // 遅延ロード後のデータも含めて最新のprice_dataを参照
    const priceData = (this.priceMatrix && this.priceMatrix.price_data) || this.priceData || [];
    const variationCode = this.buildVariationCode(paper, printColor);
    
    const deliveries = new Set();
    priceData.forEach(entry => {
      if (entry.variation_code === variationCode && entry.quantity === quantity && entry.delivery_time) {
        deliveries.add(entry.delivery_time);
      }
    });
    
    // 納期を適切な順序にソート
    const deliveryOrder = ['20営業日', '15営業日', '10営業日', '9営業日', '8営業日', '7営業日', '6営業日', '5営業日', '4営業日', '3営業日', '2営業日', '翌営業日'];
    const sortedDeliveries = Array.from(deliveries).sort((a, b) => {
      const indexA = deliveryOrder.indexOf(a);
      const indexB = deliveryOrder.indexOf(b);
      // 両方とも未定義の場合は数値でソート（"10日"が"2日"より後に来るように）
      if (indexA === -1 && indexB === -1) {
        const numA = parseInt(String(a).match(/\d+/)?.[0] || '0', 10);
        const numB = parseInt(String(b).match(/\d+/)?.[0] || '0', 10);
        return numB - numA; // 降順（納期が長い順）
      }
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexB - indexA; // 長い納期から短い納期へ（降順）
    });
    
    return sortedDeliveries;
  }
  
  /**
   * variation_codeを構築
   */
  /**
   * variation_codeの正規化（全角/半角の統一）
   * CSVとShopifyで表記揺れがある場合に対応
   */
  normalizeVariationCode(code) {
    if (!code) return code;
    
    return code
      // 全角括弧を半角に
      .replace(/（/g, '(')
      .replace(/）/g, ')')
      // 全角スラッシュを半角に
      .replace(/／/g, '/')
      // 連続スペースを単一に
      .replace(/\s+/g, ' ')
      .trim();
  }
  
  /**
   * 【新設計】variation_codeを構築
   *
   * variation_code_configメタフィールドがある場合はそれに従ってコードを生成
   * 設定がない場合は従来のフォールバックロジックを使用
   */
  buildVariationCode(paper, printColor, pageCount = null) {

    // ★冊子商品の防御的処理: Shopifyオプションが「本文紙質」または「ページ範囲」のみの場合、
    //   paper/printColor 引数には表紙紙質・ページ範囲などが混入することがある。
    //   また variation_code_config が設定されていても、price_data 上の variation_code は
    //   表紙紙質名（例: 「コート135kg」）のみのケースがあり、config 側で getFirstAvailableVariationCode() の
    //   フォールバックに落ちると price_data 先頭の別の表紙紙質（例:「アートポスト180kg」）が返ってしまい、
    //   価格表と合計金額が不整合になる。そのため config より先に coverPaper を優先する。
    const shopifyOpts = (typeof window !== 'undefined' && window.productData?.shopify_options) || [];
    const hasBodyPaperOpt = shopifyOpts.some(o => o && o.name === '本文紙質');
    const hasPageRangeOpt = shopifyOpts.some(o => o && o.name === 'ページ範囲');
    const hasPaperOpt = shopifyOpts.some(o => o && o.name === '用紙');
    const hasPrintColorOpt = shopifyOpts.some(o => o && o.name === '印刷色数');
    const isBookletLike = (hasBodyPaperOpt || hasPageRangeOpt) && !hasPaperOpt && !hasPrintColorOpt;

    if (isBookletLike) {
      const coverPaperEarly = this.currentSelection?.coverPaper;
      const priceDataEarly = (this.priceMatrix && this.priceMatrix.price_data) || this.priceData || [];
      const hasBodyPaperFieldEarly = priceDataEarly.some(p => p.body_paper !== undefined && p.body_paper !== null);
      const sampleVariationCodesEarly = [...new Set(
        priceDataEarly.map(p => p.variation_code).filter(v => v && v !== window.VARIATION_CODE_STANDARD)
      )];

      // 新形式（body_paperフィールドあり）: variation_code は表紙紙質のみ。coverPaperが指定されていればそれを返す
      if (hasBodyPaperFieldEarly && coverPaperEarly && sampleVariationCodesEarly.includes(coverPaperEarly)) {
        return coverPaperEarly;
      }
    }

    // 【新設計】variation_code_configがある場合はそれを優先使用
    if (this.variationCodeConfig) {
      const code = this.buildVariationCodeFromConfig(paper, printColor, pageCount);
      if (code) {
        return code;
      }
    }

    if (isBookletLike) {
      const coverPaper = this.currentSelection?.coverPaper;
      const bodyPaper = this.currentSelection?.bodyPaper;
      const priceData = (this.priceMatrix && this.priceMatrix.price_data) || this.priceData || [];
      const sampleVariationCodes = [...new Set(
        priceData.map(p => p.variation_code).filter(v => v && v !== window.VARIATION_CODE_STANDARD)
      )];
      const hasBodyPaperField = priceData.some(p => p.body_paper !== undefined && p.body_paper !== null);

      // 新しいデータ形式（body_paperフィールドあり）: variation_code は表紙紙質のみ
      if (hasBodyPaperField && coverPaper) {
        return coverPaper;
      }

      // 旧形式（coverPaper_bodyPaper）
      if (coverPaper && bodyPaper) {
        const combo = `${coverPaper}${window.VARIATION_CODE_SEPARATOR}${bodyPaper}`;
        if (sampleVariationCodes.includes(combo)) {
          return combo;
        }
      }

      // 「本文と同じ」形式（モノクロ統合モード）
      if (sampleVariationCodes.includes('本文と同じ')) {
        return '本文と同じ';
      }

      // coverPaper だけで一致
      if (coverPaper && sampleVariationCodes.includes(coverPaper)) {
        return coverPaper;
      }

      // ページ範囲形式（ページ範囲オプションのみの商品）
      const pageRangePattern = /^\d+-\d+P$/;
      if (sampleVariationCodes.some(code => pageRangePattern.test(code))) {
        const currentPageCount = pageCount !== null ? parseInt(pageCount) :
                                 this.currentSelection?.pageCount ? parseInt(this.currentSelection.pageCount) : null;
        if (currentPageCount !== null && !isNaN(currentPageCount)) {
          const matchingRange = this.findPageRangeForCount(currentPageCount, sampleVariationCodes);
          if (matchingRange) return matchingRange;
        }
        const firstRange = sampleVariationCodes.find(code => pageRangePattern.test(code));
        if (firstRange) return firstRange;
      }

      // 何もマッチしなければフォールバック
      return coverPaper || this.getFirstAvailableVariationCode();
    }

    // 以下は従来のフォールバックロジック（variation_code_configがない場合）
    return this.buildVariationCodeLegacy(paper, printColor, pageCount);
  }

  /**
   * variation_code_configに基づいてvariation_codeを生成
   */
  buildVariationCodeFromConfig(paper, printColor, pageCount) {
    const config = this.variationCodeConfig;
    if (!config || !config.format) return null;

    const separator = config.separator || '_';

    switch (config.format) {
      case 'fixed':
        // 固定値（標準、本文と同じ等）
        return config.defaultValue || window.VARIATION_CODE_STANDARD;

      case 'paper_only':
        // 用紙名のみ
        return this.currentSelection?.coverPaper || paper || this.getFirstAvailableVariationCode();

      case 'size_only':
        // サイズのみ（伝票等）
        return this.currentSelection?.size || this.getFirstAvailableVariationCode();

      case 'pageRange': {
        // ページ範囲形式（8-12P等）
        const currentPageCount = pageCount !== null ? parseInt(pageCount) :
                                 this.currentSelection?.pageCount ? parseInt(this.currentSelection.pageCount) : null;
        if (currentPageCount !== null) {
          const priceData = this.priceData || [];
          const sampleCodes = [...new Set(priceData.map(p => p.variation_code).filter(v => v))];
          const matchingRange = this.findPageRangeForCount(currentPageCount, sampleCodes);
          if (matchingRange) return matchingRange;
        }
        return this.getFirstAvailableVariationCode();
      }

      case 'paper_printColor': {
        // 用紙_印刷色 形式
        const paperVal = this.currentSelection?.coverPaper || paper;
        // printColorが渡されていない場合、currentSelectionから取得
        const printColorVal = printColor || this.currentSelection?.printColor;
        if (paperVal && printColorVal) {
          return `${paperVal}${separator}${printColorVal}`;
        }
        // paperのみでprintColorがない場合、price_dataから適切なコードを探す
        if (paperVal) {
          const priceData = this.priceData || [];
          const matchingCode = priceData.find(p =>
            p.variation_code && p.variation_code.startsWith(paperVal + window.VARIATION_CODE_SEPARATOR)
          )?.variation_code;
          if (matchingCode) return matchingCode;
        }
        return this.getFirstAvailableVariationCode();
      }

      case 'paper_halfCut': {
        // 用紙_ハーフカット 形式（ステッカー商品用）
        const stickerPaper = this.currentSelection?.paper || paper;
        const halfCut = this.currentSelection?.halfCut;
        if (stickerPaper && halfCut) {
          return `${stickerPaper}${separator}${halfCut}`;
        }
        // paperのみでhalfCutがない場合、price_dataから適切なコードを探す
        if (stickerPaper) {
          const priceData = this.priceData || [];
          const matchingCode = priceData.find(p =>
            p.variation_code && p.variation_code.startsWith(stickerPaper + window.VARIATION_CODE_SEPARATOR)
          )?.variation_code;
          if (matchingCode) return matchingCode;
        }
        return this.getFirstAvailableVariationCode();
      }

      case 'fsc_paper_printColor': {
        // FSC認証用紙{紙質}_{印刷色} 形式
        const fscPaper = this.currentSelection?.coverPaper || paper;
        const fscPrintColor = printColor || this.currentSelection?.printColor;
        if (fscPaper && fscPrintColor) {
          const prefix = config.paperPrefix || 'FSC認証用紙';
          const paperPart = fscPaper.startsWith(prefix) ? fscPaper : `${prefix}${fscPaper}`;
          return `${paperPart}${separator}${fscPrintColor}`;
        }
        // fscPaperのみの場合、price_dataから適切なコードを探す
        if (fscPaper) {
          const priceData = this.priceData || [];
          const prefix = config.paperPrefix || 'FSC認証用紙';
          const paperPart = fscPaper.startsWith(prefix) ? fscPaper : `${prefix}${fscPaper}`;
          const matchingCode = priceData.find(p =>
            p.variation_code && p.variation_code.startsWith(paperPart + window.VARIATION_CODE_SEPARATOR)
          )?.variation_code;
          if (matchingCode) return matchingCode;
        }
        return this.getFirstAvailableVariationCode();
      }

      case 'page_printColor_paper': {
        // ページ数_印刷色_用紙 形式
        const pc = pageCount !== null ? pageCount : this.currentSelection?.pageCount;
        const pcp = this.currentSelection?.coverPaper || paper;
        const pcPrintColor = printColor || this.currentSelection?.printColor;
        const suffix = config.pageCountSuffix || 'ページ';
        if (pc && pcPrintColor && pcp) {
          return `${pc}${suffix}${separator}${pcPrintColor}${separator}${pcp}`;
        }
        return this.getFirstAvailableVariationCode();
      }

      default:
        // 不明な形式 - フォールバックを使用
        return null;
    }
  }

  /**
   * 最初の利用可能なvariation_codeを取得（フォールバック用）
   */
  getFirstAvailableVariationCode() {
    // 遅延ロード後のデータも含めて最新のprice_dataを参照
    const priceData = (this.priceMatrix && this.priceMatrix.price_data) || this.priceData || [];
    const codes = [...new Set(priceData.map(p => p.variation_code).filter(v => v && v !== window.VARIATION_CODE_STANDARD))];
    return codes[0] || window.VARIATION_CODE_STANDARD;
  }

  /**
   * 従来のvariation_code生成ロジック（フォールバック）
   */
  buildVariationCodeLegacy(paper, printColor, pageCount = null) {
    // 遅延ロード後のデータも含めて最新のprice_dataを参照
    const priceData = (this.priceMatrix && this.priceMatrix.price_data) || this.priceData || [];
    const sampleVariationCodes = [...new Set(priceData.map(p => p.variation_code).filter(v => v && v !== window.VARIATION_CODE_STANDARD))];

    // ★【統一設計】新しいデータ形式の判定（body_paperフィールドがあるか）
    // body_paperフィールドがある場合、variation_codeは表紙紙質のみ（body_paperは別途フィルタリング）
    const hasBodyPaperField = priceData.some(p => p.body_paper !== undefined && p.body_paper !== null);

    // 【中綴じ冊子対応（旧形式）】「表紙紙質_本文紙質」形式のvariation_codeを構築
    // 中綴じ冊子形式の判定（XXX_YYYkg形式のvariation_codeが存在するか）
    const hasBookletFormat = sampleVariationCodes.some(code =>
      code.includes(window.VARIATION_CODE_SEPARATOR) && /\d+kg$/i.test(code)
    );

    const coverPaper = this.currentSelection?.coverPaper;
    let bodyPaper = this.currentSelection?.bodyPaper;

    // ★【優先】新しいデータ形式: body_paperフィールドがある場合はvariation_codeは表紙紙質のみ
    if (hasBodyPaperField && coverPaper) {
      if (sampleVariationCodes.includes(coverPaper)) {
        return coverPaper;
      }
    }

    // 【後方互換性】旧形式: body_paperフィールドがなく、variation_codeが「表紙紙質_本文紙質」形式
    // bodyPaperが未設定だが中綴じ冊子形式の場合、price_dataから推測
    if (coverPaper && !bodyPaper && hasBookletFormat && !hasBodyPaperField) {
      const matchingCode = sampleVariationCodes.find(code => {
        if (!code.includes(window.VARIATION_CODE_SEPARATOR)) return false;
        const parts = code.split(window.VARIATION_CODE_SEPARATOR);
        return parts[0] === coverPaper;
      });

      if (matchingCode) {
        bodyPaper = matchingCode.split(window.VARIATION_CODE_SEPARATOR)[1];
        if (this.currentSelection) {
          this.currentSelection.bodyPaper = bodyPaper;
        }
      }
    }

    // 【後方互換性】旧形式: coverPaper_bodyPaper形式のvariation_code
    if (coverPaper && bodyPaper && !hasBodyPaperField) {
      const bookletVariationCode = `${coverPaper}${window.VARIATION_CODE_SEPARATOR}${bodyPaper}`;
      const codeExists = sampleVariationCodes.includes(bookletVariationCode);
      if (codeExists || hasBookletFormat) {
        return bookletVariationCode;
      }
    }

    // 【無線綴じ冊子対応】coverPaperのみが設定されている場合
    // window.isPaperKey を使用して紙質判定
    if (coverPaper && !hasBookletFormat) {
      const isPaperFormat = window.isPaperKey ? window.isPaperKey(coverPaper) : /^(コート|マット|上質|アート|書籍用紙|アートポスト)\d+kg$/i.test(coverPaper);
      const isSameAsBody = coverPaper === '本文と同じ';
      if (isPaperFormat || isSameAsBody || sampleVariationCodes.includes(coverPaper) || sampleVariationCodes.length === 0) {
        return coverPaper;
      }
    }

    // 「本文と同じ」がvariation_codeに存在し、他のオプションがない場合
    if (sampleVariationCodes.includes('本文と同じ') && sampleVariationCodes.length === 1) {
      return '本文と同じ';
    }

    // 用紙のみのパターン
    // window.isPaperKey を使用して紙質判定
    const hasPaperVariations = sampleVariationCodes.some(code => window.isPaperKey ? window.isPaperKey(code) : /^(コート|マット|上質|アート|書籍用紙)\d+kg$/.test(code));

    if (hasPaperVariations && (!paper || paper === '' || paper === 'null') && (!printColor || printColor === '' || printColor === 'null')) {
      const firstPaper = sampleVariationCodes.find(code => window.isPaperKey ? window.isPaperKey(code) : /^(コート|マット|上質|アート|書籍用紙)\d+kg$/.test(code));
      if (firstPaper) return firstPaper;
    }

    // ページ範囲形式
    const pageRangePattern = /^\d+-\d+P$/;
    const hasPageRangeVariations = sampleVariationCodes.some(code => pageRangePattern.test(code));

    if (hasPageRangeVariations && (!paper || paper === '' || paper === 'null') && (!printColor || printColor === '' || printColor === 'null')) {
      const currentPageCount = pageCount !== null ? parseInt(pageCount) :
                               this.currentSelection?.pageCount ? parseInt(this.currentSelection.pageCount) : null;

      if (currentPageCount !== null && !isNaN(currentPageCount)) {
        const matchingRange = this.findPageRangeForCount(currentPageCount, sampleVariationCodes);
        if (matchingRange) return matchingRange;
      }

      const firstPageRange = sampleVariationCodes.find(code => pageRangePattern.test(code));
      if (firstPageRange) return firstPageRange;
    }

    // 正規化マッチング
    const normalizedToOriginal = new Map();
    for (const code of sampleVariationCodes) {
      normalizedToOriginal.set(this.normalizeVariationCode(code), code);
    }

    const variant1 = [paper, printColor].filter(p => p && p !== '' && p !== 'null').join(window.VARIATION_CODE_SEPARATOR);
    const variant2 = [printColor, paper].filter(p => p && p !== '' && p !== 'null').join(window.VARIATION_CODE_SEPARATOR);
    const normalizedVariant1 = this.normalizeVariationCode(variant1);
    const normalizedVariant2 = this.normalizeVariationCode(variant2);

    let code;
    if (sampleVariationCodes.includes(variant1)) {
      code = variant1;
    } else if (sampleVariationCodes.includes(variant2)) {
      code = variant2;
    } else if (normalizedToOriginal.has(normalizedVariant1)) {
      code = normalizedToOriginal.get(normalizedVariant1);
    } else if (normalizedToOriginal.has(normalizedVariant2)) {
      code = normalizedToOriginal.get(normalizedVariant2);
    } else if (paper && paper !== '' && paper !== 'null' && printColor && printColor !== '' && printColor !== 'null') {
      code = variant1;
    } else if (paper && paper !== '' && paper !== 'null') {
      code = paper;
    } else if (printColor && printColor !== '' && printColor !== 'null') {
      code = printColor;
    } else {
      code = window.VARIATION_CODE_STANDARD;
    }

    return code;
  }
  
  /**
   * ページ数から対応するページ範囲variation codeを見つける
   * @param {number} pageCount - ページ数（例: 8, 12, 16）
   * @param {Array} variationCodes - 利用可能なvariation codes（例: ['8-12P', '16-20P']）
   * @returns {string|null} - マッチするページ範囲、見つからない場合はnull
   */
  findPageRangeForCount(pageCount, variationCodes) {
    // ページ範囲形式のコードをパースして範囲をチェック
    const pageRangePattern = /^(\d+)-(\d+)P$/;
    
    for (const code of variationCodes) {
      const match = code.match(pageRangePattern);
      if (match) {
        const minPage = parseInt(match[1]);
        const maxPage = parseInt(match[2]);
        
        // ページ数が範囲内にあるかチェック
        if (pageCount >= minPage && pageCount <= maxPage) {
          return code;
        }
      }
    }
    
    return null;
  }
  
  /**
   * UIの初期化
   * 
   * Note: サイズ・用紙・印刷色のボタンは、product.dynamic-calculator.liquid の
   * window.showConfigDialog で処理されるため、ここではイベントリスナーを登録しません。
   */
  initializeUI() {
    
    // 数量選択（重複登録を防ぐ）
    const quantityInput = document.getElementById('quantity-input');
    if (quantityInput && !quantityInput._dcpListenerAttached) {
      quantityInput._dcpListenerAttached = true;
      quantityInput.addEventListener('change', (e) => {
        // 常に最新のインスタンスを参照（reinitialize後も正しく動作するように）
        const calculator = window.priceCalculator;
        if (!calculator) return;

        // 再帰的な呼び出しを防ぐ
        if (calculator._isUpdatingQuantity) return;
        calculator._isUpdatingQuantity = true;

        calculator.currentSelection.quantity = parseInt(e.target.value) || calculator.currentSelection.quantity;

        // モバイル用のセレクターも同期
        const mobileQuantitySelect = document.getElementById('mobile-quantity-select');
        if (mobileQuantitySelect) {
          mobileQuantitySelect.value = e.target.value;
        }

        calculator.updateUI();

        calculator._isUpdatingQuantity = false;
      });
    }
    
    // 納期選択（重複登録を防ぐ）
    const deliverySelect = document.getElementById('delivery-select');
    if (deliverySelect && !deliverySelect._dcpListenerAttached) {
      deliverySelect._dcpListenerAttached = true;
      deliverySelect.addEventListener('change', (e) => {
        // 常に最新のインスタンスを参照（reinitialize後も正しく動作するように）
        const calculator = window.priceCalculator;
        if (!calculator) return;

        // 再帰的な呼び出しを防ぐ
        if (calculator._isUpdatingDelivery) return;
        calculator._isUpdatingDelivery = true;

        calculator.currentSelection.delivery = e.target.value;

        // モバイル用のセレクターも同期
        const mobileDeliverySelect = document.getElementById('mobile-delivery-select');
        if (mobileDeliverySelect) {
          mobileDeliverySelect.value = e.target.value;
        }

        calculator.updateUI();

        calculator._isUpdatingDelivery = false;
      });
    }
    
    // データ種別選択（重複登録を防ぐ）
    const dataTypeSelect = document.getElementById('data-type-select');
    if (dataTypeSelect && !dataTypeSelect._dcpListenerAttached) {
      dataTypeSelect._dcpListenerAttached = true;
      dataTypeSelect.addEventListener('change', (e) => {
        // 常に最新のインスタンスを参照
        const calculator = window.priceCalculator;
        if (!calculator) return;

        calculator.currentSelection.dataType = e.target.value;
        calculator.updateUI();
      });
    }
    
  }
  
  /**
   * オプション調整値を適用
   * ProductOptionsManagerから呼び出される
   * @param {number} priceAdjustment - 価格調整額（円）
   * @param {number} leadTimeAdjustment - 納期調整（日数）
   */
  applyOptionAdjustments(priceAdjustment, leadTimeAdjustment) {
    
    this.currentSelection.optionPriceAdjustment = priceAdjustment || 0;
    this.currentSelection.optionLeadTimeAdjustment = leadTimeAdjustment || 0;
    
    // UIを更新
    this.updateUI();
  }
  
  /**
   * UIの更新
   */
  updateUI() {
    // 遅延ロード後に数量・納期が有効な値か確認し、必要に応じて補正
    const hasPriceData = this.priceMatrix && this.priceMatrix.price_data && this.priceMatrix.price_data.length > 0;

    if (hasPriceData) {
      const paper = this.currentSelection.paper || '';
      const printColor = this.currentSelection.printColor || '';

      // 利用可能な数量を取得
      const availableQuantities = this.getAvailableQuantities(paper, printColor);

      if (availableQuantities.length > 0 && !availableQuantities.includes(this.currentSelection.quantity)) {
        this.currentSelection.quantity = availableQuantities[0];
        // セレクターも更新
        const quantityInput = document.getElementById('quantity-input');
        if (quantityInput) quantityInput.value = this.currentSelection.quantity;
      }

      // 利用可能な納期を取得
      const availableDeliveries = this.getAvailableDeliveries(paper, printColor, this.currentSelection.quantity);

      if (availableDeliveries.length > 0 && !availableDeliveries.includes(this.currentSelection.delivery)) {
        this.currentSelection.delivery = availableDeliveries[0];
        // セレクターも更新
        const deliverySelect = document.getElementById('delivery-select');
        if (deliverySelect) deliverySelect.value = this.currentSelection.delivery;
      }
    }

    // 価格を計算
    const result = this.calculatePrice();
    
    if (result) {
      
      // フッター表示の更新（PC版）
      const footerQuantity = document.getElementById('footer-quantity');
      const footerPrintType = document.getElementById('footer-print-type');
      const footerDelivery = document.getElementById('footer-delivery');
      const footerTotalPrice = document.getElementById('footer-total-price');
      
      if (footerQuantity) {
        footerQuantity.textContent = `${this.currentSelection.quantity.toLocaleString()} 部`;
      }
      
      if (footerPrintType) {
        // 共通ユーティリティを使用してフッター表示を統一
        if (window.ProductDisplayUtils && typeof window.ProductDisplayUtils.getFooterPrintTypeDisplay === 'function') {
          footerPrintType.textContent = window.ProductDisplayUtils.getFooterPrintTypeDisplay();
        } else {
          // フォールバック
          const printMethod = this.currentSelection.printMethod || 'オンデマンド';
          const printColor = this.currentSelection.printColor || '片面カラー';
          footerPrintType.textContent = `${printMethod} / ${printColor}`;
        }
      }
      
      if (footerDelivery) {
        // 選択中の納期を取得（価格表と連動）
        const rawDelivery = this.currentSelection.delivery || '3日';
        
        // 商品の発送日計算方式を取得
        const productCalcType = window.productDeliveryCalcType || 'business_days';
        
        // 納期を正しい形式に変換（価格表と同じ計算になるように）
        // 「1日」→「1営業日」（営業日計算の場合）
        let baseDelivery = rawDelivery;
        if (productCalcType === 'business_days' && !rawDelivery.includes('営業日')) {
          const dayMatch = rawDelivery.match(/(\d+)日/);
          if (dayMatch) {
            baseDelivery = dayMatch[1] + '営業日';
          }
        }
        
        // オプションによる納期調整を取得
        let optionLeadTimeAdjustment = 0;
        if (window.productOptionsManager && typeof window.productOptionsManager.getOptionAdjustments === 'function') {
          const adjustments = window.productOptionsManager.getOptionAdjustments();
          if (adjustments && typeof adjustments.leadTime === 'number') {
            optionLeadTimeAdjustment = adjustments.leadTime;
          }
        }
        
        // 発送予定日を計算
        let deliveryDateStr = `${baseDelivery}後`;
        if (window.businessDayCalculator && window.businessDayCalculator.loaded) {
          try {
            const options = { calcType: productCalcType };
            const deliveryResult = window.businessDayCalculator.calculateDeliveryDate(baseDelivery, optionLeadTimeAdjustment, options);
            if (deliveryResult && deliveryResult.formatted) {
              deliveryDateStr = deliveryResult.formatted;
            }
          } catch (e) {
            console.warn('[DynamicPriceCalculator] Error calculating delivery date:', e);
          }
        }
        footerDelivery.textContent = deliveryDateStr;
      }
      
      if (footerTotalPrice) {
        footerTotalPrice.textContent = `${Math.round(result.total).toLocaleString()}`;
      }
      
      // フッター料金明細の更新
      const footerProductPrice = document.getElementById('footer-product-price');
      const footerOptionPrice = document.getElementById('footer-option-price');
      
      // オプション価格を取得
      let optionPrice = 0;
      if (window.productOptionsManager && typeof window.productOptionsManager.getOptionAdjustments === 'function') {
        const adjustments = window.productOptionsManager.getOptionAdjustments();
        if (adjustments && typeof adjustments.price === 'number') {
          optionPrice = adjustments.price;
        }
      }
      
      // 商品価格 = 合計 - オプション価格
      const productPrice = Math.round(result.total - optionPrice);
      
      if (footerProductPrice) {
        footerProductPrice.textContent = productPrice.toLocaleString();
      }
      
      if (footerOptionPrice) {
        footerOptionPrice.textContent = Math.round(optionPrice).toLocaleString();
      }

      // 色タイプ表示の更新（PC版フッター）
      this.updateFooterColorType();

      // モバイルフッター表示の更新
      const mobileFooterQuantity = document.getElementById('mobile-footer-quantity');
      const mobileFooterPrintType = document.getElementById('mobile-footer-print-type');
      const mobileFooterDelivery = document.getElementById('mobile-footer-delivery');
      const mobileFooterTotalPrice = document.getElementById('mobile-footer-total-price');
      
      if (mobileFooterQuantity) {
        mobileFooterQuantity.textContent = `${this.currentSelection.quantity.toLocaleString()} 部`;
      }
      
      if (mobileFooterPrintType) {
        // 共通ユーティリティを使用してフッター表示を統一
        if (window.ProductDisplayUtils && typeof window.ProductDisplayUtils.getFooterPrintTypeDisplay === 'function') {
          mobileFooterPrintType.textContent = window.ProductDisplayUtils.getFooterPrintTypeDisplay();
        } else {
          // フォールバック
          const printMethod = this.currentSelection.printMethod || 'オンデマンド';
          const printColor = this.currentSelection.printColor || '片面カラー';
          mobileFooterPrintType.textContent = `${printMethod} / ${printColor}`;
        }
      }
      
      if (mobileFooterDelivery) {
        // 選択中の納期を取得（価格表と連動）
        const rawDelivery = this.currentSelection.delivery || '3日';
        
        // 商品の発送日計算方式を取得
        const productCalcType = window.productDeliveryCalcType || 'business_days';
        
        // 納期を正しい形式に変換（価格表と同じ計算になるように）
        // 「1日」→「1営業日」（営業日計算の場合）
        let baseDelivery = rawDelivery;
        if (productCalcType === 'business_days' && !rawDelivery.includes('営業日')) {
          const dayMatch = rawDelivery.match(/(\d+)日/);
          if (dayMatch) {
            baseDelivery = dayMatch[1] + '営業日';
          }
        }
        
        // オプションによる納期調整を取得
        let optionLeadTimeAdjustment = 0;
        if (window.productOptionsManager && typeof window.productOptionsManager.getOptionAdjustments === 'function') {
          const adjustments = window.productOptionsManager.getOptionAdjustments();
          if (adjustments && typeof adjustments.leadTime === 'number') {
            optionLeadTimeAdjustment = adjustments.leadTime;
          }
        }
        
        // 発送予定日を計算
        let mobileDeliveryDateStr = `${baseDelivery}後`;
        if (window.businessDayCalculator && window.businessDayCalculator.loaded) {
          try {
            const options = { calcType: productCalcType };
            const deliveryResult = window.businessDayCalculator.calculateDeliveryDate(baseDelivery, optionLeadTimeAdjustment, options);
            if (deliveryResult && deliveryResult.formatted) {
              mobileDeliveryDateStr = deliveryResult.formatted;
            }
          } catch (e) {
            console.warn('[DynamicPriceCalculator] Error calculating mobile delivery date:', e);
          }
        }
        mobileFooterDelivery.textContent = mobileDeliveryDateStr;
      }
      
      if (mobileFooterTotalPrice) {
        mobileFooterTotalPrice.textContent = `${Math.round(result.total).toLocaleString()} 円`;
      }
      
      // セレクターの更新（PC版）
      const quantityInput = document.getElementById('quantity-input');
      const deliverySelect = document.getElementById('delivery-select');
      
      if (quantityInput && quantityInput.value != this.currentSelection.quantity) {
        quantityInput.value = this.currentSelection.quantity;
      }
      
      if (deliverySelect && deliverySelect.value != this.currentSelection.delivery) {
        deliverySelect.value = this.currentSelection.delivery;
      }
      
      // セレクターの更新（モバイル版）
      const mobileQuantitySelect = document.getElementById('mobile-quantity-select');
      const mobileDeliverySelect = document.getElementById('mobile-delivery-select');
      
      if (mobileQuantitySelect && mobileQuantitySelect.value != this.currentSelection.quantity) {
        mobileQuantitySelect.value = this.currentSelection.quantity;
      }
      
      if (mobileDeliverySelect && mobileDeliverySelect.value != this.currentSelection.delivery) {
        mobileDeliverySelect.value = this.currentSelection.delivery;
      }
      
      // 価格内訳の更新
      this.updatePriceBreakdown(result);

      // 商品ヘッダー（タイトル下）の更新
      // 部数に応じた印刷方式表示を反映
      if (typeof window.updateGoodsHeader === 'function') {
        window.updateGoodsHeader();
      }

      // 価格表とセレクターを更新（用紙・印刷色数が変更された場合）
      if (typeof window.updatePriceTable === 'function') {
        window.updatePriceTable();
      }
      if (typeof window.updateSelectorsFromData === 'function') {
        window.updateSelectorsFromData();
      }
      if (typeof window.updateMobileSelectorsFromData === 'function') {
        window.updateMobileSelectorsFromData();
      }

      // 価格表のセル選択を更新
      this.updatePriceTableSelection();
    } else {
      console.warn('[DynamicPriceCalculator] Price calculation returned null');
    }
  }

  /**
   * 価格表のセル選択を更新
   */
  updatePriceTableSelection() {
    const quantity = this.currentSelection.quantity;
    const delivery = this.currentSelection.delivery;

    // 全てのセルから選択状態を削除
    const allCells = document.querySelectorAll('.price-cell');
    allCells.forEach(cell => {
      cell.classList.remove('selected');
    });

    // 新しく選択されたセルをハイライト
    const selector = `.price-cell[data-quantity="${quantity}"][data-delivery="${delivery}"]`;
    const selectedCell = document.querySelector(selector);
    if (selectedCell) {
      selectedCell.classList.add('selected');
    }
  }
  
  /**
   * 価格内訳の更新
   * @param {Object} result - 計算結果
   */
  updatePriceBreakdown(result) {
    const breakdownContainer = document.getElementById('price-breakdown');
    if (!breakdownContainer) return;
    
    let html = '<div class="price-breakdown-content">';
    html += `<div class="breakdown-item">`;
    html += `  <span class="breakdown-label">基本料金:</span>`;
    html += `  <span class="breakdown-value">¥${Math.round(result.basePrice || 0).toLocaleString()}</span>`;
    html += `</div>`;
    
    if (result.sizeMultiplier && result.sizeMultiplier !== 1.0) {
      html += `<div class="breakdown-item">`;
      html += `  <span class="breakdown-label">サイズ倍率 (${this.currentSelection.size}):</span>`;
      html += `  <span class="breakdown-value">×${result.sizeMultiplier}</span>`;
      html += `</div>`;
    }
    
    if (result.paperMultiplier && result.paperMultiplier !== 1.0) {
      html += `<div class="breakdown-item">`;
      html += `  <span class="breakdown-label">用紙倍率 (${this.currentSelection.paper}):</span>`;
      html += `  <span class="breakdown-value">×${result.paperMultiplier}</span>`;
      html += `</div>`;
    }
    
    if (result.printColorMultiplier && result.printColorMultiplier !== 1.0) {
      html += `<div class="breakdown-item">`;
      html += `  <span class="breakdown-label">印刷色倍率 (${this.currentSelection.printColor}):</span>`;
      html += `  <span class="breakdown-value">×${result.printColorMultiplier}</span>`;
      html += `</div>`;
    }
    
    if (result.tax) {
      html += `<div class="breakdown-item">`;
      html += `  <span class="breakdown-label">消費税:</span>`;
      html += `  <span class="breakdown-value">¥${Math.round(result.tax).toLocaleString()}</span>`;
      html += `</div>`;
    }
    
    html += `<div class="breakdown-item total">`;
    html += `  <span class="breakdown-label"><strong>合計:</strong></span>`;
    html += `  <span class="breakdown-value"><strong>¥${Math.round(result.total).toLocaleString()}</strong></span>`;
    html += `</div>`;
    html += '</div>';
    
    breakdownContainer.innerHTML = html;
  }
  
  /**
   * 価格を計算
   * @returns {Object|null} 計算結果 {basePrice, total, tax, ...} または null
   */
  calculatePrice() {
    
    try {
      // 価格マトリックスから基本価格を取得
      const basePrice = this.getBasePriceFromMatrix();
      
      if (!basePrice || basePrice === 0) {
        // 遅延ロード中は警告を抑制
        if (!(window.needsLazyLoad && !window._lazyLoadDataReady)) {
          console.warn('[DynamicPriceCalculator] Base price not found or is zero');
          console.warn('Quantity:', this.currentSelection.quantity);
          console.warn('Delivery:', this.currentSelection.delivery);
          console.warn('Price matrix entries:', this.priceMatrix.price_data?.length || 0);
        }
        return {
          basePrice: 0,
          subtotal: 0,
          tax: 0,
          total: 0,
          sizeMultiplier: 1.0,
          paperMultiplier: 1.0,
          printColorMultiplier: 1.0
        };
      }
      
      
      // オプション倍率を取得
      // 注意: 現在のデータ構造では、用紙と印刷色数は既にバリエーションコードに含まれているため、
      // multiplierは使用しません。サイズと印刷方法もデータに含まれていないため、価格計算には影響しません。
      
      // 小計を計算（basePriceをそのまま使用）
      // 注意: CSVの価格はすでに税込価格なので、税金計算は不要
      let subtotal = basePrice;
      
      // 加工オプションの追加料金
      const processingCost = this.getProcessingCost();
      subtotal += processingCost;
      
      // データ種別の追加料金
      const dataTypeCost = this.getDataTypeCost();
      subtotal += dataTypeCost;
      
      // オプション価格調整を追加（従量制の場合は毎回再計算）
      let optionPriceAdjustment = this.currentSelection.optionPriceAdjustment || 0;
      // ProductOptionsManagerから最新の調整値を取得（従量制対応）
      if (window.productOptionsManager && typeof window.productOptionsManager.getOptionAdjustments === 'function') {
        const adjustments = window.productOptionsManager.getOptionAdjustments();
        if (adjustments && typeof adjustments.price === 'number') {
          optionPriceAdjustment = adjustments.price;
        }
      }
      subtotal += optionPriceAdjustment;
      
      // 税金計算（CSVの価格は既に税込なので、税金は含まれている）
      // 税額を逆算して表示用に計算
      const taxRate = 0.1;
      const tax = Math.floor(subtotal * (taxRate / (1 + taxRate)));
      
      // 合計（CSVの価格が既に税込なので、subtotalがそのまま合計）
      const total = subtotal;
      
      // バリエーションコードを生成（ログ用、共通メソッドを使用）
      const variationCode = this.buildVariationCode(this.currentSelection.paper, this.currentSelection.printColor);
      
      
      return {
        basePrice,
        subtotal,
        tax,
        total,
        sizeMultiplier: 1.0, // サイズは価格に影響しない
        paperMultiplier: 1.0, // 用紙はバリエーションコードに含まれている
        printColorMultiplier: 1.0, // 印刷色数もバリエーションコードに含まれている
        processingCost,
        dataTypeCost
      };
      
    } catch (error) {
      console.error('[DynamicPriceCalculator] Error calculating price:', error);
      return null;
    }
  }
  
  /**
   * 価格マトリックスから基本価格を取得
   * @returns {number} 基本価格
   */
  /**
   * 【統一設計】価格マトリックスからベース価格を取得
   * - variation_code: 用紙_印刷色 形式
   * - page_count: フィールドとして検索条件に追加
   */
  getBasePriceFromMatrix() {
    if (!this.priceMatrix.price_data || this.priceMatrix.price_data.length === 0) {
      // 遅延ロード中はまだデータがないのでエラーではなく警告
      if (window.needsLazyLoad && !window._lazyLoadInitialized) {
      } else {
        console.error('[DynamicPriceCalculator] ERROR: Price data not available');
      }
      return 0;
    }
    
    const quantity = this.currentSelection.quantity;
    const delivery = this.currentSelection.delivery;
    const paper = this.currentSelection.paper;
    const printColor = this.currentSelection.printColor;
    const pageCount = this.currentSelection.pageCount;
    const size = this.currentSelection.size;
    const bodyPaper = this.currentSelection.bodyPaper;

    // 【統一設計】variation_code: 用紙_印刷色 形式（pageCountはフィールドとして検索）
    // ★修正: ページ範囲形式の商品（中綴じ冊子など）のためにpageCountを渡す
    const variationCode = this.buildVariationCode(paper, printColor, pageCount);

    // ページ数のターゲット値を計算
    const hasPageCountOptions = window.pageCountOptions && window.pageCountOptions.options && window.pageCountOptions.options.length > 0;
    let targetPageCount = null;
    if (pageCount !== null && pageCount !== undefined && pageCount !== '') {
      targetPageCount = parseInt(pageCount, 10);
      if (isNaN(targetPageCount)) targetPageCount = null;
    } else if (hasPageCountOptions) {
      // default が設定されている場合はそれを使用、なければ options[0] を使用
      const defaultVal = window.pageCountOptions.default || window.pageCountOptions.options[0];
      // オブジェクト形式に対応
      const numVal = (typeof defaultVal === 'object' && defaultVal !== null)
        ? parseInt(defaultVal.value, 10)
        : parseInt(defaultVal, 10);
      if (!isNaN(numVal)) {
        targetPageCount = numVal;
      }
    }

    // データ全体に有効な page_count があるか（価格表 indexer と同じ判定）
    const dataHasPageCount = this.priceMatrix.price_data.some(
      entry => entry.page_count !== undefined && entry.page_count !== null
    );

    /**
     * page_count マッチ判定。
     * データに page_count がある商品で target がある場合、
     * page_count 未設定/null の行はマッチさせない（古い16P価格などを誤って拾わない）。
     */
    const matchesPageCount = (entry) => {
      if (!dataHasPageCount || targetPageCount === null) return true;
      if (entry.page_count === undefined || entry.page_count === null) return false;
      return Number(entry.page_count) === Number(targetPageCount);
    };

    // サイズフィルタリングが必要かどうかを判定（price_matrixにsizeフィールドがある場合）
    const hasSizeInPriceData = this.priceMatrix.price_data.some(entry => entry.size !== undefined);

    // 本文紙質フィルタリングが必要かどうかを判定（price_matrixにbody_paperフィールドがある場合）
    const hasBodyPaperInPriceData = this.priceMatrix.price_data.some(entry => entry.body_paper !== undefined);
    
    // バリエーションコード、数量、納期にマッチする価格データを検索
    // page_countがある場合はそれも条件に追加
    // sizeがある場合はそれも条件に追加
    // body_paperがある場合はそれも条件に追加
    let priceEntry = this.priceMatrix.price_data.find(entry => {
      const matchBase = entry.variation_code === variationCode &&
                        Number(entry.quantity) === Number(quantity) &&
                        entry.delivery_time === delivery;

      const matchPageCount = matchesPageCount(entry);

      // データにsizeがある場合は、必ずsizeもマッチさせる
      let matchSize = true;
      if (hasSizeInPriceData && entry.size !== undefined && size) {
        matchSize = entry.size === size;
      }

      // データにbody_paperがある場合は、必ずbody_paperもマッチさせる（中綴じ・無線綴じ冊子用）
      let matchBodyPaper = true;
      if (hasBodyPaperInPriceData && entry.body_paper !== undefined && bodyPaper) {
        matchBodyPaper = entry.body_paper === bodyPaper;
      }

      return matchBase && matchPageCount && matchSize && matchBodyPaper;
    });
    
    if (priceEntry) {
      return priceEntry.base_price || 0;
    }
    
    // 完全一致がない場合は、最も近い数量を探す
    
    // 同じバリエーションコードと納期で数量が近いものを探す（page_count, size, body_paperも考慮）
    const sameVariationAndDeliveryEntries = this.priceMatrix.price_data.filter(entry => {
      const matchBase = entry.variation_code === variationCode &&
                        entry.delivery_time === delivery;
      const matchPageCount = matchesPageCount(entry);
      // データにsizeがある場合は、必ずsizeもマッチさせる
      let matchSize = true;
      if (hasSizeInPriceData && entry.size !== undefined && size) {
        matchSize = entry.size === size;
      }
      // データにbody_paperがある場合は、必ずbody_paperもマッチさせる
      let matchBodyPaper = true;
      if (hasBodyPaperInPriceData && entry.body_paper !== undefined && bodyPaper) {
        matchBodyPaper = entry.body_paper === bodyPaper;
      }
      return matchBase && matchPageCount && matchSize && matchBodyPaper;
    });
    
    if (sameVariationAndDeliveryEntries.length > 0) {
      // 数量でソート（最も近い数量を使用）
      sameVariationAndDeliveryEntries.sort((a, b) => Math.abs(a.quantity - quantity) - Math.abs(b.quantity - quantity));
      return sameVariationAndDeliveryEntries[0].base_price || 0;
    }
    
    // バリエーションコードだけで検索（納期が一致しない場合、page_count, size, body_paperも考慮）
    const sameVariationEntries = this.priceMatrix.price_data.filter(entry => {
      const matchBase = entry.variation_code === variationCode;
      const matchPageCount = matchesPageCount(entry);
      // データにsizeがある場合は、必ずsizeもマッチさせる
      let matchSize = true;
      if (hasSizeInPriceData && entry.size !== undefined && size) {
        matchSize = entry.size === size;
      }
      // データにbody_paperがある場合は、必ずbody_paperもマッチさせる
      let matchBodyPaper = true;
      if (hasBodyPaperInPriceData && entry.body_paper !== undefined && bodyPaper) {
        matchBodyPaper = entry.body_paper === bodyPaper;
      }
      return matchBase && matchPageCount && matchSize && matchBodyPaper;
    });
    
    if (sameVariationEntries.length > 0) {
      // 数量と納期の両方で最も近いものを探す
      sameVariationEntries.sort((a, b) => {
        const diffA = Math.abs(a.quantity - quantity);
        const diffB = Math.abs(b.quantity - quantity);
        return diffA - diffB;
      });
      console.warn('[DynamicPriceCalculator] Variation code matched but delivery not found, using:', sameVariationEntries[0]);
      return sameVariationEntries[0].base_price || 0;
    }
    
    // 【統一設計】フォールバック禁止 - 見つからない場合はエラー
    console.error('[DynamicPriceCalculator] ERROR: No matching price entry found');
    console.error('[DynamicPriceCalculator] Search conditions:', {
      variationCode,
      quantity,
      delivery,
      targetPageCount,
      size,
      bodyPaper
    });
    console.error('[DynamicPriceCalculator] Available variation codes:',
      [...new Set(this.priceMatrix.price_data.map(e => e.variation_code))].slice(0, 10).join(', '));
    console.error('[DynamicPriceCalculator] Available body_papers:',
      [...new Set(this.priceMatrix.price_data.map(e => e.body_paper).filter(Boolean))].join(', '));
    console.error('[DynamicPriceCalculator] Sample price data:', this.priceMatrix.price_data.slice(0, 3));
    
    // フォールバックせず0を返す（UIでエラー表示される）
    return 0;
  }
  
  /**
   * サイズ倍率を取得
   * @returns {number} サイズ倍率
   */
  getSizeMultiplier() {
    // 現在のデータ構造ではサイズ情報は価格データに含まれていないため、常に1.0を返す
    const size = this.currentSelection.size;
    const multiplier = this.optionMultipliers.size_multipliers?.[size] || 1.0;
    return 1.0; // サイズは価格に影響しない
  }
  
  /**
   * 用紙倍率を取得
   * @returns {number} 用紙倍率
   */
  getPaperMultiplier() {
    const paper = this.currentSelection.paper;
    const multiplier = this.optionMultipliers.paper_multipliers?.[paper] || 1.0;
    return multiplier;
  }
  
  /**
   * 印刷色倍率を取得
   * @returns {number} 印刷色倍率
   */
  getPrintColorMultiplier() {
    const printColor = this.currentSelection.printColor;
    const multiplier = this.optionMultipliers.print_color_multipliers?.[printColor] || 1.0;
    return multiplier;
  }
  
  /**
   * 加工オプションの追加料金を取得
   * @returns {number} 加工料金
   */
  getProcessingCost() {
    // TODO: 加工オプションの実装
    return 0;
  }
  
  /**
   * データ種別の追加料金を取得
   * @returns {number} データ種別料金
   */
  getDataTypeCost() {
    // TODO: データ種別の実装
    return 0;
  }
  
  /**
   * 現在の選択状態を取得
   * @returns {Object} 選択状態
   */
  getCurrentSelection() {
    return { ...this.currentSelection };
  }
  
  /**
   * 選択状態を更新
   * @param {Object} updates - 更新する項目
   */
  updateSelection(updates) {
    Object.assign(this.currentSelection, updates);
    this.updateUI();
  }

  /**
   * フッターの色タイプ表示を更新
   */
  updateFooterColorType() {
    const colorTypeRow = document.getElementById('footer-color-type-row');
    const colorTypeEl = document.getElementById('footer-color-type');
    const mobileColorTypeRow = document.getElementById('mobile-footer-color-type-row');
    const mobileColorTypeEl = document.getElementById('mobile-footer-color-type');

    const colorSelection = window.colorTypeSelection;

    // 色選択がない場合は非表示
    if (!colorSelection || (!colorSelection.front && !colorSelection.back)) {
      if (colorTypeRow) colorTypeRow.style.display = 'none';
      if (mobileColorTypeRow) mobileColorTypeRow.style.display = 'none';
      return;
    }

    // 色タイプHTML生成
    const html = this._generateColorTypeHTML(colorSelection);

    // PC版フッター更新
    if (colorTypeRow && colorTypeEl) {
      colorTypeRow.style.display = '';
      colorTypeEl.innerHTML = html;
    }

    // モバイル版フッター更新
    if (mobileColorTypeRow && mobileColorTypeEl) {
      mobileColorTypeRow.style.display = '';
      mobileColorTypeEl.innerHTML = html;
    }
  }

  /**
   * 色タイプ表示用のHTMLを生成
   * @param {Object} colorSelection - 色選択情報
   * @returns {string} HTML文字列
   */
  _generateColorTypeHTML(colorSelection) {
    const parts = [];

    if (colorSelection.front) {
      const dotStyle = this._getColorDotStyle(colorSelection.front.hex, colorSelection.front.id);
      parts.push(`<span class="footer-color-item">表面: <span class="color-dot-inline" style="${dotStyle}"></span>${colorSelection.front.name}</span>`);
    }

    if (colorSelection.back) {
      const dotStyle = this._getColorDotStyle(colorSelection.back.hex, colorSelection.back.id);
      parts.push(`<span class="footer-color-item">裏面: <span class="color-dot-inline" style="${dotStyle}"></span>${colorSelection.back.name}</span>`);
    }

    return parts.join(' ');
  }

  /**
   * 色ドットのインラインスタイルを生成
   * @param {string} hex - 色のHEXコード
   * @param {string} colorId - 色ID
   * @returns {string} インラインスタイル
   */
  _getColorDotStyle(hex, colorId) {
    if (colorId === 'cmyk') {
      return 'background: conic-gradient(#00bcd4 0deg 90deg, #e91e63 90deg 180deg, #ffeb3b 180deg 270deg, #1a1a1a 270deg 360deg);';
    }
    return `background-color: ${hex || '#999'};`;
  }
}

// グローバルスコープに公開
if (typeof window !== 'undefined') {
  window.DynamicPriceCalculator = DynamicPriceCalculator;
}

